<script setup lang="ts">
import { ref, watch, onMounted } from 'vue'
import { useI18n } from '@/composables/useI18n'
import { useFilters } from '@/composables/useFilters'
import { debounce } from '@/utils'
import type { FilterChip } from '@/types'

const { t } = useI18n()
const { filterChips, toggleChip, removeChip, buildSuggestions, buildChipFromInput, formatChipLabel } = useFilters()

const inputValue = ref('')
const suggestions = ref<FilterChip[]>([])
const suggestionIndex = ref(-1)
const showSuggestions = ref(false)
const inputRef = ref<HTMLInputElement | null>(null)

const updateSuggestions = debounce((value: string) => {
  suggestionIndex.value = -1
  suggestions.value = buildSuggestions(value)
}, 150)

function onInput(e: Event) {
  const value = (e.target as HTMLInputElement).value
  inputValue.value = value
  showSuggestions.value = true
  updateSuggestions(value)
}

function onFocus() {
  showSuggestions.value = true
  suggestions.value = buildSuggestions(inputValue.value)
}

function onBlur() {
  setTimeout(() => {
    showSuggestions.value = false
    suggestions.value = []
  }, 150)
}

function applyCurrentSelection() {
  const active = suggestionIndex.value >= 0 ? suggestions.value[suggestionIndex.value] : null
  const chipData = active || buildChipFromInput(inputValue.value)
  if (!chipData) return
  toggleChip(chipData.kind, chipData.value, chipData.label)
  inputValue.value = ''
  suggestionIndex.value = -1
  suggestions.value = buildSuggestions('')
}

function onKeydown(e: KeyboardEvent) {
  if (e.key === 'ArrowDown' && suggestions.value.length) {
    e.preventDefault()
    suggestionIndex.value = (suggestionIndex.value + 1) % suggestions.value.length
  } else if (e.key === 'ArrowUp' && suggestions.value.length) {
    e.preventDefault()
    suggestionIndex.value = (suggestionIndex.value - 1 + suggestions.value.length) % suggestions.value.length
  } else if (e.key === 'Enter') {
    e.preventDefault()
    applyCurrentSelection()
  } else if (e.key === 'Tab' && suggestionIndex.value >= 0) {
    e.preventDefault()
    applyCurrentSelection()
  } else if (e.key === 'Backspace' && !inputValue.value && filterChips.value.length) {
    removeChip(filterChips.value.length - 1)
    suggestions.value = buildSuggestions('')
  }
}

function selectSuggestion(index: number) {
  const item = suggestions.value[index]
  if (!item) return
  toggleChip(item.kind, item.value, item.label)
  inputValue.value = ''
  suggestionIndex.value = -1
  suggestions.value = buildSuggestions('')
}
</script>

<template>
  <div class="filter-stack">
    <div class="filter-input-wrapper">
      <div class="chip-container" aria-live="polite">
        <span
          v-for="(chip, i) in filterChips"
          :key="`${chip.kind}:${chip.value}`"
          :class="['chip', `chip-${chip.kind}`]"
        >
          <span>{{ chip.kind.toUpperCase() }} · {{ formatChipLabel(chip.kind, chip.value) }}</span>
          <button type="button" aria-label="Remove" @click="removeChip(i)">×</button>
        </span>
      </div>
      <input
        ref="inputRef"
        type="text"
        class="filter-input"
        :placeholder="t('filter-placeholder')"
        :aria-label="t('filter-placeholder')"
        autocomplete="off"
        :value="inputValue"
        @input="onInput"
        @focus="onFocus"
        @blur="onBlur"
        @keydown="onKeydown"
      >
    </div>
    <div
      v-if="showSuggestions && suggestions.length"
      class="suggestion-box open"
      role="listbox"
      :aria-label="t('filter-placeholder')"
    >
      <button
        v-for="(item, index) in suggestions"
        :key="`${item.kind}:${item.value}`"
        type="button"
        :class="['suggestion-item', { active: index === suggestionIndex }]"
        @mousedown.prevent="selectSuggestion(index)"
      >
        <span class="suggestion-kind">{{ item.kind.charAt(0).toUpperCase() + item.kind.slice(1) }}</span>
        <span class="suggestion-label">{{ item.label || formatChipLabel(item.kind, item.value) }}</span>
      </button>
    </div>
  </div>
</template>
